import {useEffect, useState, useRef} from "react";
import moment from "moment";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ToastCalendar from "@toast-ui/calendar";
import "@toast-ui/calendar/dist/toastui-calendar.min.css";
import {useTranslation} from "react-i18next";
// -
import ModalEvent from "./ModalEvent";
import ModalEditRoute from "./ModalEditRoute";
import ModalCreateRoute from "./ModalCreateRoute";
import {capitalize} from "core/utils/Texts";
import WasteSelectorTypes from "components/waste-icons/WasteSelectorTypes";
import generateEventsForRange from "./generateEvents";
import generateRoutesForRange from "./generateRoutes";
import calendarTemplates from "./calendarTemplate";
import axiApi from "ApiCaller";

const CalendarTui = ({items, setShowModalForm}) => {
    const {t: _Tc} = useTranslation("choices");
    const {t: _Td} = useTranslation("datestimes");
    const [view, setView] = useState("month");
    const [showModal, setShowModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [currentMonth, setCurrentMonth] = useState("");
    const [titleDateRange, setTitleDateRange] = useState({start: "", end: ""});
    const dateRange = useRef({start: null, end: null});
    const [activeCalendars, setActiveCalendars] = useState(["0"]);
    const [calendars, setCalendars] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [showModalEdit, setShowModalEdit] = useState(false);
    const [showModalCreate, setShowModalCreate] = useState(false);

    const viewButtons = useRef({day: null, week: null, month: null});
    const calendarRef = useRef(null); // ref al nodo DOM contenedor
    const calendarInstance = useRef(null); // instancia vanilla de @toast-ui/calendar

    class CalendarModel {
        constructor(type) {
            this.id = WasteSelectorTypes[type].calendar.id;
            this.name = _Tc("wastes." + type);
            this.color = WasteSelectorTypes[type].calendar.color;
            this.borderColor = WasteSelectorTypes[type].borderColor;
        }
    }

    // ── Helper ───────────────────────────────────────────────────────────────
    const getCalendar = () => calendarInstance.current;

    const updateMonthAndRange = () => {
        const cal = getCalendar();
        if (!cal) return;
        const currentDate = cal.getDate();
        const monthName = currentDate.toDate().toLocaleString("default", {month: "long"});
        setCurrentMonth(capitalize(monthName) + " - " + currentDate.toDate().getFullYear());
        const startDate = cal.getDateRangeStart().toDate();
        const endDate = cal.getDateRangeEnd().toDate();
        dateRange.current.start = moment(startDate);
        dateRange.current.end = moment(new Date(endDate.setHours(23, 59, 59, 999)));
        setTitleDateRange({
            start: dateRange.current.start.format("DD-MM-YYYY"),
            end: dateRange.current.end.format("DD-MM-YYYY"),
        });
        createEventsSchedule();
    };

    const getFilteredSchedules = () => schedules.filter((schedule) => activeCalendars.includes(schedule.calendarId));

    const schedulesRef = useRef([]);
    const itemsRef = useRef([]);

    // Keep refs updated
    useEffect(() => {
        schedulesRef.current = schedules;
    }, [schedules]);

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    // ── Handlers de eventos del calendario ──────────────────────────────────
    const handleClickEvent = (e) => {
        const id = e.event.id;

        // Realized routes have ids starting with 'route-'
        if (id.startsWith("route-")) {
            const idSplit = id.split("-");
            const schedule = {
                router: idSplit[1],
                event: id,
            };
            console.log("CalendarTui::handleClickEvent -> Realized route detected:", schedule);
            setSelectedEvent(schedule);
            setShowModalEdit(true);
            return false;
        }

        // Planned events
        const eventFound = schedulesRef.current.find((schedule) => String(schedule.id) === String(id));
        if (!eventFound) {
            console.warn("CalendarTui::handleClickEvent -> Event not found in schedules for id:", id);
            return;
        }

        console.log("CalendarTui::handleClickEvent -> Planned event found:", eventFound);

        const prog = itemsRef.current.find((item) => String(item.id) === String(eventFound.progId));
        if (!prog) {
            console.warn("CalendarTui::handleClickEvent -> Programming not found for progId:", eventFound.progId);
            return;
        }

        const routeTemplate = prog.routeslst.find((route) => String(route.item.id) === String(eventFound.routeId));
        if (!routeTemplate) {
            console.warn("CalendarTui::handleClickEvent -> Route template not found for routeId:", eventFound.routeId);
            return;
        }

        const scheduleData = {
            event: eventFound,
            prog: prog,
            route: routeTemplate,
        };

        // Get date from event - robust way for ToastUI Calendar v2
        let dateString;
        try {
            const startDate = e.event.start.toDate();
            dateString = moment(startDate).format("YYYY-MM-DD");
        } catch (err) {
            console.warn("CalendarTui::handleClickEvent -> Error getting date from event.start:", err);
            // Fallback to current schedules start date if available
            dateString = moment(eventFound.start).format("YYYY-MM-DD");
        }

        console.log("CalendarTui::handleClickEvent -> Final schedule data:", scheduleData, "Date:", dateString);

        if (scheduleData.route.type === "s") {
            const virtual = {
                scheduleId: eventFound.id,
                prog: scheduleData.prog.id,
                route: scheduleData.route.item.id,
                created: dateString,
            };
            console.log("CalendarTui::handleClickEvent -> Opening ModalCreateRoute with:", virtual);
            setSelectedEvent(virtual);
            setShowModalCreate(true);
        } else {
            console.log("CalendarTui::handleClickEvent -> Opening ModalEvent");
            setSelectedEvent(scheduleData);
            setShowModal(true);
        }
    };

    const handleDayClick = (info) => {
        const {start} = info;
        const cal = getCalendar();
        if (cal) {
            cal.setDate(start);
            viewButtons.current[view]?.classList.add("btn-outline-primary");
            viewButtons.current[view]?.classList.remove("btn-primary");
            viewButtons.current["day"]?.classList.add("btn-primary");
            viewButtons.current["day"]?.classList.remove("btn-outline-primary");
            cal.changeView("day");
            setView("day");
        }
    };

    // ── Montaje: crear instancia vanilla ────────────────────────────────────
    useEffect(() => {
        if (!calendarRef.current) return;

        calendarInstance.current = new ToastCalendar(calendarRef.current, {
            defaultView: "month",
            usageStatistics: false,
            isReadOnly: false,
            useDetailPopup: false,
            useFormPopup: false,
            month: {
                dayNames: [_Td("days_short.0"), _Td("days_short.1"), _Td("days_short.2"), _Td("days_short.3"), _Td("days_short.4"), _Td("days_short.5"), _Td("days_short.6")],
                startDayOfWeek: 1,
            },
            week: {
                dayNames: [_Td("days_short.0"), _Td("days_short.1"), _Td("days_short.2"), _Td("days_short.3"), _Td("days_short.4"), _Td("days_short.5"), _Td("days_short.6")],
                taskView: false,
                showTimezoneCollapseButton: true,
                eventView: ["time"],
                startDayOfWeek: 1,
                showNowIndicator: true,
            },
            day: {
                taskView: false,
                eventView: ["time"],
                showNowIndicator: true,
            },
            template: calendarTemplates,
        });

        calendarInstance.current.on("clickEvent", handleClickEvent);
        calendarInstance.current.on("selectDateTime", handleDayClick);

        // Inicializar botones y rango de fechas
        viewButtons.current = {
            day: document.querySelector("#button-calendar-day"),
            week: document.querySelector("#button-calendar-week"),
            month: document.querySelector("#button-calendar-month"),
        };
        const cal = calendarInstance.current;
        const currentDate = cal.getDate();
        const monthName = currentDate.toDate().toLocaleString("default", {month: "long"});
        setCurrentMonth(capitalize(monthName) + " - " + currentDate.toDate().getFullYear());
        const startDate = cal.getDateRangeStart().toDate();
        const endDate = cal.getDateRangeEnd().toDate();
        dateRange.current.start = moment(startDate);
        dateRange.current.end = moment(new Date(endDate.setHours(23, 59, 59, 999)));
        setTitleDateRange({
            start: dateRange.current.start.format("DD-MM-YYYY"),
            end: dateRange.current.end.format("DD-MM-YYYY"),
        });

        return () => {
            calendarInstance.current?.destroy();
            calendarInstance.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Efectos reactivos ────────────────────────────────────────────────────
    useEffect(() => {
        createEventsSchedule();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    useEffect(() => {
        updateMonthAndRange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    useEffect(() => {
        const cal = getCalendar();
        if (!cal) return;
        cal.clear();
        cal.createEvents(getFilteredSchedules());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCalendars, schedules]);

    // ── Carga de datos ───────────────────────────────────────────────────────
    const createEventsSchedule = () => {
        // Sin programaciones también se pintan las rutas reales (p. ej. las instanciadas sin programación)
        if (items === null) return;
        axiApi
            .post("/api/distribution/routes/range/", {
                date_start: dateRange.current.start.format("YYYY-MM-DD"),
            })
            .then((resp) => {
                //console.log(resp.data);
                let calendarsIds = [];
                let activeCalendars_ = [];
                let calendars_ = [];
                let routesIds = [];
                items.forEach((item) => {
                    item.routeslst.forEach((route) => {
                        if (!calendarsIds.includes(route.item.waste_type)) {
                            calendarsIds.push(route.item.waste_type);
                            let calendar = new CalendarModel(route.item.waste_type);
                            activeCalendars_.push(calendar.id);
                            calendars_.push(calendar);
                        }
                    });
                });
                resp.data.items.forEach((item) => {
                    if (!calendarsIds.includes(item.waste_type)) {
                        calendarsIds.push(item.waste_type);
                        let calendar = new CalendarModel(item.waste_type);
                        activeCalendars_.push(calendar.id);
                        calendars_.push(calendar);
                    }
                });
                setCalendars([...calendars_]);
                setActiveCalendars([...activeCalendars_]);
                let routes_ = generateRoutesForRange(resp.data.items, routesIds);
                let schedules_ = generateEventsForRange(items, dateRange.current, routesIds);
                setSchedules([...routes_, ...schedules_]);
            });
    };

    // ── Handlers de navegación ───────────────────────────────────────────────
    const handleCalendarToggle = (id) => {
        setActiveCalendars((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
    };

    const changeView = (e) => {
        let newView = e.target.dataset.view;
        viewButtons.current[view]?.classList.add("btn-outline-primary");
        viewButtons.current[view]?.classList.remove("btn-primary");
        viewButtons.current[newView]?.classList.add("btn-primary");
        viewButtons.current[newView]?.classList.remove("btn-outline-primary");
        getCalendar()?.changeView(newView);
        setView(newView);
    };

    const handleClickPrev = () => {
        getCalendar()?.prev();
        updateMonthAndRange();
    };
    const handleClickToday = () => {
        getCalendar()?.today();
        updateMonthAndRange();
    };
    const handleClickNext = () => {
        getCalendar()?.next();
        updateMonthAndRange();
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="container">
            <div className="row">
                <div className="mb-3 col-12">
                    <div className="row">
                        <div className="col-md col-12">
                            <button onClick={handleClickPrev} className="btn btn-outline-secondary btn-sm me-2">
                                <FontAwesomeIcon icon="arrow-left" />
                            </button>
                            <button onClick={handleClickToday} className="btn btn-outline-secondary btn-sm me-2">
                                {_Td("periods.today")}
                            </button>
                            <button onClick={handleClickNext} className="btn btn-outline-secondary btn-sm">
                                <FontAwesomeIcon icon="arrow-right" />
                            </button>
                        </div>
                        <div className="col-md col-12 text-center">
                            <strong className="ms-3 h3">{currentMonth}</strong>
                            {view === "day" ? (
                                <span className="ms-3">{titleDateRange.start}</span>
                            ) : (
                                <span className="ms-3">
                                    {titleDateRange.start} ~ {titleDateRange.end}
                                </span>
                            )}
                        </div>
                        <div className="col-md col-12 text-end">
                            <button
                                data-view="day"
                                onClick={changeView}
                                className="btn btn-outline-primary btn-sm me-2"
                                id="button-calendar-day">
                                {_Td("periods.day")}
                            </button>
                            <button
                                data-view="week"
                                onClick={changeView}
                                className="btn btn-outline-primary btn-sm me-2"
                                id="button-calendar-week">
                                {_Td("periods.week")}
                            </button>
                            <button
                                data-view="month"
                                onClick={changeView}
                                className="btn btn-primary btn-sm"
                                id="button-calendar-month">
                                {_Td("periods.month")}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col border-end border-top pt-3" style={{maxWidth: "fit-content"}}>
                        <h5>{_Td("labels.fractions")}</h5>
                        {calendars.map((calendar) => (
                            <div key={calendar.id} className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`calendar-${calendar.id}`}
                                    checked={activeCalendars.includes(calendar.id)}
                                    onChange={() => handleCalendarToggle(calendar.id)}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor={`calendar-${calendar.id}`}
                                    style={{color: calendar.borderColor}}>
                                    {calendar.name}
                                </label>
                            </div>
                        ))}
                    </div>

                    {/* El calendario vanilla se monta en este div — no es un componente React */}
                    <div
                        ref={calendarRef}
                        className={`col routes-calendar toastui-calendar-view-${view} ps-0`}
                        style={{height: "calc(100vh - 210px)"}}
                    />
                </div>

                {showModal && <ModalEvent show={showModal} setShow={setShowModal} schedule={selectedEvent} aria-modal="true" />}
                {showModalEdit && (
                    <ModalEditRoute
                        show={showModalEdit}
                        setShow={setShowModalEdit}
                        event={selectedEvent}
                        schedules={schedules}
                        setSchedules={setSchedules}
                        aria-modal="true"
                    />
                )}
                {showModalCreate && (
                    <ModalCreateRoute
                        show={showModalCreate}
                        setShow={setShowModalCreate}
                        virtual={selectedEvent}
                        schedules={schedules}
                        setSchedules={setSchedules}
                        aria-modal="true"
                    />
                )}
            </div>
        </div>
    );
};

export default CalendarTui;
